/**
 * API client for segmentation builder workflow
 */

import axios from 'axios';

const API_BASE = '/api/v1/chat';

export interface UploadResponse {
  sessionId: string;
}

export interface UploadCompleteData {
  sessionId: string;
  uploadedFile: string;
  qualFiles: Array<{ path: string; filename: string }>;
  projectTitle: string;
}

export interface ConfigData {
  focal_brand_ids: string[];
  volume_filter: number;
  period_split: string;
  regimen_coverage_pct: number;
}

export interface StrategyData {
  strategy_key?: string;
  nl_input?: string;
  qual_mode: 'data_driven' | 'qual_informed' | 'qual_primary';
}

export const segmentationBuilderApi = {
  /**
   * Step 1: Upload a file (or select a server file) via multipart/form-data.
   * Returns the sessionId created by the segmentation API.
   */
  async uploadFile(file?: File, serverFile?: string): Promise<UploadResponse> {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE}/segmentation-builder/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    if (serverFile) {
      const formData = new FormData();
      formData.append('serverFile', serverFile);
      const response = await axios.post(`${API_BASE}/segmentation-builder/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    throw new Error('Either file or serverFile is required');
  },

  /**
   * Step 2: Notify the backend that the upload is done and resume the workflow.
   * Sends only lightweight metadata (no File object).
   */
  async submitUploadComplete(queryIdentifier: string, data: UploadCompleteData): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/upload-complete`, {
      queryIdentifier,
      sessionId: data.sessionId,
      uploadedFile: data.uploadedFile,
      qualFiles: data.qualFiles,
      projectTitle: data.projectTitle,
    });
  },

  /**
   * Submit configuration and advance workflow
   */
  async submitConfiguration(queryIdentifier: string, config: ConfigData): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/configuration-complete`, {
      queryIdentifier,
      config,
    });
  },

  /**
   * Continue from EDA to strategy phase
   */
  async continueFromEDA(queryIdentifier: string): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/eda-continue`, {
      queryIdentifier,
    });
  },

  /**
   * Submit strategy and advance workflow
   */
  async submitStrategy(queryIdentifier: string, strategyData: StrategyData): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/strategy-complete`, {
      queryIdentifier,
      strategyData,
    });
  },

  /**
   * Accept or edit refinement
   */
  async submitRefinement(
    queryIdentifier: string,
    action: 'accept' | 'edit',
    editedObjective?: string
  ): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/refinement-complete`, {
      queryIdentifier,
      action,
      editedObjective,
    });
  },

  /**
   * Continue from features to run segmentation
   */
  async continueFromFeatures(queryIdentifier: string, updatedWeights?: Record<string, number>): Promise<void> {
    await axios.post(`${API_BASE}/segmentation-builder/features-complete`, {
      queryIdentifier,
      updatedWeights,
    });
  },

  /**
   * Export results
   */
  async exportResults(
    queryIdentifier: string,
    exportType: 'excel' | 'powerpoint' | 'all'
  ): Promise<{ downloadUrl: string }> {
    const response = await axios.post(`${API_BASE}/segmentation-builder/export`, {
      queryIdentifier,
      exportType,
    });
    return response.data;
  },
};
